import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Phase 16 — auth-cookie bridge.
//
// During the bridge period we accept BOTH:
//   * the legacy localStorage JWT (sent via Authorization: Bearer …), and
//   * the new httpOnly `tagedin_auth` cookie (set by the backend on login).
//
// When the cookie path is in use, state-changing requests must echo the
// JS-readable `tagedin_csrf` cookie back to the server in an
// `X-CSRF-Token` header (double-submit pattern). The interceptor below
// handles that automatically.
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  // Send cookies on every request. Same-origin via the Kubernetes ingress so
  // no CORS preflight changes are required.
  withCredentials: true,
});

const AUTH_COOKIE_NAME = 'tagedin_auth';
const CSRF_COOKIE_NAME = 'tagedin_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

// Lightweight cookie reader. We only need to read the public CSRF cookie —
// the auth cookie is httpOnly and intentionally inaccessible to JS.
export function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split(';') : [];
  for (let i = 0; i < parts.length; i += 1) {
    const c = parts[i].trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

export function hasAuthCookie() {
  // The auth cookie itself is httpOnly so we can't see it directly. The CSRF
  // companion cookie is set together with it, so its presence is a reliable
  // proxy for "we have a cookie session".
  return !!readCookie(CSRF_COOKIE_NAME);
}

// Attach CSRF header on state-changing requests. The backend only enforces
// this when the request is cookie-authenticated, so it is harmless on Bearer
// requests too.
const STATE_CHANGING = new Set(['post', 'put', 'patch', 'delete']);
api.interceptors.request.use((config) => {
  const m = (config.method || 'get').toLowerCase();
  if (STATE_CHANGING.has(m)) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf && !config.headers[CSRF_HEADER]) {
      config.headers[CSRF_HEADER] = csrf;
    }
  }
  return config;
});

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('tagedin_token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('tagedin_token');
  }
}

export function getStoredToken() {
  return localStorage.getItem('tagedin_token');
}

// Apply stored token at module load so refreshes keep auth (legacy path).
// New cookie sessions are picked up automatically via withCredentials.
const stored = getStoredToken();
if (stored) {
  api.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
}

// Phase 23 — email verification REMOVED. The previous response interceptor
// that bounced unverified-detail responses to /verify-email has been deleted
// alongside the backend gate it served. The interceptor is intentionally
// not replaced — there is no longer any 403 email_unverified path to
// intercept.

// Used by Auth screens / context to know whether some authentication is
// available before we even hit /auth/me.
export function hasAnyAuth() {
  return !!getStoredToken() || hasAuthCookie();
}

export function fileUrl(fileId) {
  if (!fileId) return null;
  // Cookie sessions: same-origin browsers auto-attach the httpOnly
  // `tagedin_auth` cookie on raw `<img>`/`<video>` element fetches, so we
  // don't (and shouldn't) embed any token in the URL. Falling back to ?auth=
  // only when there's no cookie keeps legacy bearer-only clients working
  // until the bridge is fully retired.
  if (hasAuthCookie()) {
    return `${API_BASE}/files/${fileId}`;
  }
  const t = getStoredToken();
  return `${API_BASE}/files/${fileId}${t ? `?auth=${encodeURIComponent(t)}` : ''}`;
}

export function fileDownloadUrl(fileId, postId) {
  if (!fileId) return null;
  const params = new URLSearchParams();
  if (postId) params.set('post_id', postId);
  if (!hasAuthCookie()) {
    const t = getStoredToken();
    if (t) params.set('auth', t);
  }
  const qs = params.toString();
  return `${API_BASE}/files/${fileId}/download${qs ? `?${qs}` : ''}`;
}

export { AUTH_COOKIE_NAME, CSRF_COOKIE_NAME };
